/**
 * Root of the product surface + the auth composition root. Wires the shared
 * providers — `ThemeProvider` (stamps `data-theme`; tokens flip with no
 * re-render), `I18nProvider` (bound translator over the auto-detected locale),
 * and the hash `RouterProvider` (which girlfriend is active) — around the
 * {@link AuthGate}: until `4f:token` is written (and the age gate cleared) the
 * gate shows the front door, and only then mounts the companion-first
 * {@link CompanionSurface}.
 *
 * The ONE place the api client, the session store, the settings store, and the
 * in-memory token signal are constructed and wired (DIP): the token signal feeds
 * the api client + `/ws`, the session store writes it on login, and a stored
 * refresh cookie is re-hydrated on boot. The settings store owns the EFFECTIVE
 * theme / locale / timezone the providers below render — auto-detected here, then
 * hydrated from the signed-in user's persisted overrides the moment the session
 * resolves (login / boot restore), and user-editable via the settings surface.
 */
import { detectFromBrowser } from '@4f/i18n';
import { I18nProvider, ThemeProvider } from '@4f/ui';
import { createEffect, createSignal, type JSX, onMount } from 'solid-js';
import { ApiClient } from './api/client.ts';
import { apiBaseUrl } from './api/endpoints.ts';
import { CompanionSurface } from './app/CompanionSurface.tsx';
import { resolveTimeZone } from './app/timezone.ts';
import { readAuthMode } from './features/auth/auth-model.ts';
import { AuthGate } from './features/auth/index.ts';
import { browserReferralStore, captureReferralCode } from './features/referral/referral-capture.ts';
import { fallbackMessages, surfaceMessages } from './i18n/messages.ts';
import { RouterProvider } from './router/RouterProvider.tsx';
import { loadStoredToken } from './session/token-storage.ts';
import { createOutbox } from './stores/outbox.ts';
import { createSessionStore } from './stores/session.ts';
import { createSettingsStore } from './stores/settings.ts';

/** Root component — wires auth + settings; auto-detects locale/timezone as the fallback. */
export function App(): JSX.Element {
  const detectedLocale = detectFromBrowser();
  const detectedTimeZone = resolveTimeZone();

  // Referral flywheel: capture `?ref=<code>` off the landing URL (and remember it,
  // so it survives the hash router, a reload, and the login⇄signup toggle) before
  // anything else runs. Read ONCE at boot — the query is a landing fact, not
  // reactive state. Without this the whole `AuthGate → AuthSurface →
  // SignupWizard → POST /auth/register` chain carries `undefined` and every
  // invited signup is attributed to nobody.
  const referrals = browserReferralStore();
  const referralCode = captureReferralCode(globalThis.location?.search ?? '', referrals);

  // Which front door the visitor actually asked for. Marketing's CTAs point at
  // `/signup` and `/login`, but the SPA is served for ANY unknown path and read
  // `location.pathname` nowhere — so every sign-up CTA opened the SIGN-IN form,
  // and a visitor who clicked "Choose Plus" got a password field for an account
  // they did not have. Read ONCE at boot for the same reason `?ref=` is: the
  // landing path is a fact about how they arrived, not reactive state — re-reading
  // it would fight the login⇄sign-up toggle.
  const initialMode = readAuthMode(globalThis.location?.pathname ?? '');

  // Session token — held in a signal the api client + `/ws` re-read per request,
  // seeded from storage so a live session survives a reload before `restore`.
  const [token, setToken] = createSignal<string | null>(loadStoredToken());
  // Every HTTP call is issued under the `/api` prefix the production ingress
  // routes + strips. Without it the browser hits root paths that fall through to
  // the SPA container and come back as `index.html` with a 200 — HTML where JSON
  // was expected. The value is build-time configurable, never a literal here.
  // (`/ws` is NOT prefixed — see `api/endpoints.ts`.)
  //
  // `recovery` closes the loop the boot `restore()` alone left open: the access
  // token lives ~15 minutes, so without it EVERY call in the tab starts failing
  // the moment it expires while a perfectly live refresh cookie sits unused. The
  // client refreshes once and retries; the renewed session — or its definitive
  // loss — lands back in the store that owns it. The two sinks are read lazily
  // (they run per-`401`, long after both objects exist), which is what lets the
  // client and the store depend on each other without a construction cycle.
  const api = new ApiClient({
    fetch: (input, init) => fetch(input, init),
    baseUrl: apiBaseUrl(),
    token,
    recovery: {
      adopt: (renewed) => session.adoptRenewed(renewed),
      expire: () => session.expire(),
    },
  });
  const session = createSessionStore({
    api,
    setToken,
    hasToken: () => token() !== null,
    // The offline queue is device-local and account-scoped: signing out must take
    // it with the session, or the next account to sign in on this device replays
    // the previous user's unsent messages.
    onSignedOut: () => createOutbox().clear(),
  });

  // Settings — effective theme/locale/tz for the providers, with the detected
  // values as the fallback until a persisted override is adopted.
  const settings = createSettingsStore(api, {
    locale: detectedLocale,
    timeZone: detectedTimeZone,
  });

  // Read persisted settings on load: the moment the session user resolves
  // (login / boot restore), adopt their stored theme/locale/tz — auto-detect
  // fills any field they never overrode.
  createEffect(() => {
    const user = session.user();
    if (user) {
      settings.hydrate({ theme: user.theme, locale: user.locale, timezone: user.timezone });
      // The code has done its job (or this user was already a member) — drop it so
      // a later signup on this device is not misattributed to a stale inviter.
      referrals.clear();
    }
  });

  // Re-hydrate a session from the httpOnly refresh cookie on boot (drops a stale
  // access token when the edge has no live session behind it).
  onMount(() => {
    void session.restore();
  });

  return (
    <ThemeProvider override={settings.theme()}>
      <I18nProvider
        messages={surfaceMessages}
        locale={settings.locale()}
        fallback={fallbackMessages}
      >
        <RouterProvider>
          <AuthGate
            session={session}
            referralCode={referralCode ?? undefined}
            initialMode={initialMode}
          >
            <CompanionSurface
              api={api}
              token={token}
              session={session}
              settings={settings}
              locale={settings.locale()}
              timeZone={settings.timeZone()}
            />
          </AuthGate>
        </RouterProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}
